import { ChangeDetectionStrategy, Component, DestroyRef, Injectable, OnInit, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AppSettings, Settings } from 'app/app.settings';
import { AuthService } from '@core/auth';
import { ConfigService, LoggerService } from '@core/services';
import { ConfigSettings } from 'environments/ConfigSettings';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSidenavModule } from '@angular/material/sidenav';

@Component({
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSidenavModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
@Injectable()
export class LoginComponent implements OnInit {
  formGroup: FormGroup = new FormGroup({
    username: new FormControl('', [Validators.required, Validators.maxLength(250)]),
    password: new FormControl('', [Validators.maxLength(250)]),
  });
  title = '';
  config!: ConfigSettings;
  settings: Settings;
  loading = false;

  constructor(
    public appSettings: AppSettings,
    private authService: AuthService,
    private router: Router,
    private logger: LoggerService,
    private configService: ConfigService,
    private destroyRef: DestroyRef
  ) {
    this.settings = this.appSettings.settings;
  }

  async ngOnInit(): Promise<void> {
    this.configService.Config.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((config) => {
      if (!config) return;
      this.config = config;
    });

    // Check if this is a new instance
    try {
      const response = await this.authService.checkIsNewInstance().toPromise();
      if (response?.successful && response.data?.isNewInstance) {
        // Redirect to admin register screen
        this.router.navigate(['/admin-register']);
        return;
      }
    } catch (error) {
      // If check fails, continue to login screen
      this.logger.logError('Error checking if new instance', error);
    }
  }

  public get getLogo(): string {
    return this.config?.logo;
  }

  public get getClient(): string {
    if (this.config?.environmentName?.includes('local')) {
      return `${this.config?.client} - ${this.config.environmentName}`;
    }
    return this.config?.client ?? '';
  }

  public get getLoginTitle(): string {
    return (this.config?.client ?? '') + ' ' + (this.config?.appName ?? '');
  }

  public get usernameHasError(): boolean {
    return (this.formGroup.dirty || this.formGroup.touched) && (this.formGroup.get('username')?.invalid ?? false);
  }

  public get usernameError(): string {
    return this.formGroup.get('username')?.hasError('required')
      ? 'You must enter a value'
      : this.formGroup.get('username')?.hasError('maxlength')
        ? 'Email cannot be more than 250 characters long'
        : '';
  }

  public get passwordHasError(): boolean {
    return (this.formGroup.dirty || this.formGroup.touched) && (this.formGroup.get('password')?.invalid ?? false);
  }

  public get passwordError(): string {
    return this.formGroup.get('password')?.hasError('required')
      ? 'You must enter a value'
      : this.formGroup.get('password')?.hasError('maxlength')
        ? 'Password cannot be more than 250 characters long'
        : '';
  }

  async login(): Promise<void> {
    this.loading = true;
    this.formGroup.disable();
    const username = this.formGroup.get('username')?.value;
    const password = this.formGroup.get('password')?.value;

    try {
      const login = await this.authService.login(username, password);
      if (login?.successful) {
        this.logger.displaySuccess('Login Successful', 'Login');
        this.afterLogin();
      } else {
        this.logger.displayError(login.message, 'Login');
      }
    } finally {
      this.loading = false;
      this.formGroup.enable();
    }
  }

  afterLogin(): void {
    const isRetailer = this.authService.isRetailerUser();
    let defaultRoute = '/dashboard/inbound';
    if (isRetailer) {
      defaultRoute = this.authService.isInboundRetailer() ? '/dashboard/inbound' : '/dashboard/outbound';
    }
    console.debug('Navigating to:', defaultRoute);
    this.settings.loadingSpinner = false;
    this.router.navigateByUrl(defaultRoute);
  }
}
